'use client';

import { ApartmentReviewsProvider } from '@/src/components/features/property/ApartmentReviews/ApartmentReviewsProvider';
import { Fragment, useCallback, useEffect, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { css } from '@/styled-system/css';

import { useListingDetail } from '@/src/lib/hooks/query/useListingDetailQuery';
import { useRouter } from '@/src/i18n/navigation';
import { registerClientProfilerPage } from '@/src/lib/clientProfiler';
import {
  getActiveSearchHdpBackEntry,
  getActiveSearchHdpBackPath,
  prepareSearchHdpHistoryEntry,
  queueSearchHdpBackScrollRestore,
} from '@/src/lib/navigation/searchHdpHistory';
import { buildSrpFilterCarryoverHref } from '@/src/lib/navigation/srpQueryCarryover';
import { extractMaloneLotIdEncoded } from '@/src/lib/utils/listingSlugUtils';
import { useHeaderVisibility } from '@/src/layouts/HeaderVisibilityContext';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useListingCTAAvailability } from '@/src/components/features/property/hooks/useListingCTAAvailability';
import { Button, Heading, Text } from '@zillow/constellation-10';
import Footer from '@/src/components/ui/Footer';
import { ListingPage, ListingPageSkeleton } from '@/src/components/features/property/ListingPage';
import { getListingBackHref } from '@/src/components/features/property/ListingPage/listingBackHref';
import { PhotoGallerySection } from '@/src/components/features/property/PhotoGallery';
import { ListingHeaderActions } from '@/src/components/features/property/ListingHeaderActions';
import { SpokeFooterCTAs } from '@/src/components/features/property/SpokeFooterCTAs';
import { ListingAdminToolsPanelLoader } from '@/src/components/features/property/ListingAdminToolsPanel';

type HdpPageProps = {
  resourceOrMaloneId: string;
  pdpType: 'pad' | 'building';
  unit?: string | null;
  /**
   * Server-prefetched listing from the route's getHdpListing() call. When
   * present, useListingDetail uses it as initialData so the very first
   * server-rendered HTML contains the real listing content instead of a
   * skeleton — critical for crawlers on direct-load HDPs.
   */
  initialListing?: PropertyDetailListing;
  /** Server-rendered breadcrumb navigation sourced from the resolved API contract. */
  breadcrumbs?: ReactNode;
  modules?: ReactNode;
  /** Server-rendered SEO footer (by-listing), placed above the static footer. */
  seoFooter?: ReactNode;
};

// Layout effect on the client, plain effect on the server. Matches main's
// vercel HdpContent fix: hiding the global mobile header needs to happen in
// the same paint as the route/panel mount so panel transitions don't restore
// scroll against the "header visible" layout and jitter by ~56px when the
// header hides a frame later.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const hdpPageScroller = css({
  width: '100%',
  lg: {
    width: '100%',
  },
});

const errorContainer = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'layout.looser',
  textAlign: 'center',
  // eslint-disable-next-line hotpads/no-magic-numbers, constellation/no-raw-spacing-values -- keeps the error state visually centered in the HDP content well
  minHeight: '300px',
  gap: 'default',
});

const modulesLoading = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'looser',
  paddingTop: 'looser',
  paddingBottom: 'looser',
});

const modulesLoadingBlock = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'default',
  paddingBottom: 'looser',
  borderBottomWidth: 'default',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.neutral',
});

const modulesLoadingTitle = css({
  width: '40%',
  // eslint-disable-next-line hotpads/no-magic-numbers, constellation/no-raw-spacing-values -- caps skeleton title width to match expected module heading length
  maxWidth: '220px',
  height: '500',
  borderRadius: 'sm',
  background: 'bg.softest',
});

const modulesLoadingLine = css({
  height: '400',
  borderRadius: 'sm',
  background: 'bg.softest',
});

const breadcrumbsFooter = css({
  paddingTop: 'loose',
});

function ModuleRendererLoading() {
  const t = useTranslations('search');
  return (
    <div className={modulesLoading} aria-busy="true" aria-label={t('loadingListingDetails')}>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className={modulesLoadingBlock}>
          <div className={modulesLoadingTitle} />
          <div className={modulesLoadingLine} />
          <div className={modulesLoadingLine} style={{ width: '72%' }} />
        </div>
      ))}
    </div>
  );
}

const ModuleRenderer = dynamic(
  () => import('@/src/components/features/property/ModuleRenderer').then((mod) => mod.ModuleRenderer),
  {
    loading: () => <ModuleRendererLoading />,
  },
);

export function HdpPage({
  resourceOrMaloneId,
  pdpType,
  unit,
  initialListing,
  breadcrumbs,
  modules,
  seoFooter,
}: HdpPageProps) {
  const tHdp = useTranslations('hdp');
  const router = useRouter();
  const searchParams = useSearchParams();
  const maloneLotIdEncoded = extractMaloneLotIdEncoded(resourceOrMaloneId);
  const queryType = pdpType === 'building' ? 'building' : 'unit';
  const {
    data: listing,
    isLoading,
    error,
    refetch,
  } = useListingDetail(maloneLotIdEncoded, queryType, { initialData: initialListing, unit });

  const { hasCTAs } = useListingCTAAvailability(listing);
  const { setHideHeaderOnMobile } = useHeaderVisibility();
  useIsomorphicLayoutEffect(() => {
    setHideHeaderOnMobile(true);
    return () => {
      setHideHeaderOnMobile(false);
    };
  }, [setHideHeaderOnMobile]);

  useEffect(() => {
    return registerClientProfilerPage(pdpType === 'building' ? 'bdp' : 'hdp');
  }, [pdpType]);

  useIsomorphicLayoutEffect(() => {
    const isInFlowSearchHdp = prepareSearchHdpHistoryEntry();
    if (isInFlowSearchHdp) {
      const searchPath = getActiveSearchHdpBackPath();
      if (searchPath) {
        router.prefetch(searchPath as Parameters<typeof router.prefetch>[0]);
      }
    }
  }, [resourceOrMaloneId, router, unit]);

  const backHref = listing ? buildSrpFilterCarryoverHref(getListingBackHref(listing), searchParams) : '/';
  const navigateBack = useCallback(() => {
    if (prepareSearchHdpHistoryEntry()) {
      const backEntry = getActiveSearchHdpBackEntry();
      queueSearchHdpBackScrollRestore();
      if (backEntry) {
        window.history.go(-backEntry.historyDepth);
        return;
      }
    }

    router.push(backHref);
  }, [backHref, router]);
  if (isLoading) {
    return (
      <div className={hdpPageScroller} role="status" aria-live="polite" aria-label={tHdp('loadingPropertyDetails')}>
        <ListingPageSkeleton />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className={hdpPageScroller}>
        <div className={errorContainer} role="alert">
          <Heading level={1} textStyle="heading-md">
            {error ? 'Unable to load listing' : 'Listing not found'}
          </Heading>
          <Text asChild color="text.subtle">
            <p>
              {error
                ? 'Something went wrong loading this listing. Please try again.'
                : 'This listing may no longer be available.'}
            </p>
          </Text>
          {error && (
            <Button tone="neutral" emphasis="filled" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={hdpPageScroller}>
      <ApartmentReviewsProvider
        key={listing.maloneLotIdEncoded}
        listing={listing}
        snapshot={initialListing?.buildingReviewsSnapshot ?? listing.buildingReviewsSnapshot}
      >
        <ListingPage
          listing={listing}
          backHref={backHref}
          onBack={navigateBack}
          photoGallery={<PhotoGallerySection listing={listing} />}
          listingHeaderActions={({ mobileFloatingVariant }) => (
            <ListingHeaderActions
              listing={listing}
              presentation="mobileFloating"
              mobileFloatingVariant={mobileFloatingVariant}
              testIdSuffix="mobile"
            />
          )}
          desktopHeaderActions={
            <ListingHeaderActions listing={listing} presentation="mobileFloating" testIdSuffix="desktop" />
          }
          spokeFooterCTAs={!hasCTAs ? null : <SpokeFooterCTAs listing={listing} testIdSuffix="mobile" />}
          desktopFooterCTAs={!hasCTAs ? null : <SpokeFooterCTAs listing={listing} testIdSuffix="desktop" />}
          modules={modules ?? <ModuleRenderer listing={listing} />}
          pageFooter={
            <>
              {breadcrumbs ? <div className={breadcrumbsFooter}>{breadcrumbs}</div> : null}
              <Fragment key="seo-footer">{seoFooter}</Fragment>
              <Fragment key="site-footer">
                <Footer showCrossbrand={false} />
              </Fragment>
            </>
          }
        />
      </ApartmentReviewsProvider>
      <ListingAdminToolsPanelLoader listing={listing} />
    </div>
  );
}
